import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import type { ThreadMessageLike } from '@assistant-ui/react';
import { useMastraRuntime } from '@/hooks/useMastraRuntime';
import { useThreadMessages } from '@/hooks/useThreadMessages';
import { useWorkspace } from '@/providers/useWorkspace';
import { markStartupMetric } from '@/lib/startup-metrics';
import { SpinnerGlyph } from '@/components/common/SpinnerGlyph';
import { convertToThreadMessages } from './thread-message-converter';
import { fetchThreadMessages } from '@/lib/agent-threads';
import { registerAgentAttachmentRefresher } from '@/lib/agent-attachments';
import { AgentChatModeContext } from './chat-mode-context';

type ChatProviderProps = {
  children: React.ReactNode;
  mode?: 'seeker' | 'employer';
  threadId?: string;
  onThreadCreated?: (id: string) => void;
};

export function ChatProvider({
  children,
  mode = 'seeker',
  threadId,
  onThreadCreated,
}: ChatProviderProps) {
  const { t } = useTranslation('common');
  const { companyId } = useWorkspace();
  const scopedCompanyId = mode === 'employer' ? companyId : undefined;
  const { data, isLoading, isError, refetch } = useThreadMessages(threadId, mode, scopedCompanyId);
  const [transitioning, setTransitioning] = useState(false);
  const prevThreadIdRef = useRef(threadId);

  // Brief transition guard: when switching to a cached thread, the runtime
  // needs one frame to process initialMessages. Show loading to prevent
  // a flash of the empty-chat UI.
  useEffect(() => {
    if (prevThreadIdRef.current !== threadId && threadId && !isLoading) {
      setTransitioning(true);
      const timer = setTimeout(() => setTransitioning(false), 50);
      prevThreadIdRef.current = threadId;
      return () => clearTimeout(timer);
    }
    prevThreadIdRef.current = threadId;
  }, [threadId, isLoading]);

  const initialMessages = useMemo<ThreadMessageLike[]>(() => {
    if (!threadId || !data) return [];
    return convertToThreadMessages(data.messages, data.toolCalls, threadId, data.pendingApprovals);
  }, [threadId, data]);

  // An attachment's signed link expired or failed: its message asks for the
  // history once more through this (lib/agent-attachments).
  useEffect(() => {
    if (!threadId) return;
    return registerAgentAttachmentRefresher(threadId, () =>
      fetchThreadMessages(threadId, mode, scopedCompanyId),
    );
  }, [mode, scopedCompanyId, threadId]);

  useEffect(() => {
    if (!threadId || (!isLoading && !transitioning)) {
      markStartupMetric('chat_ready_ms');
    }
  }, [isLoading, threadId, transitioning]);

  if (threadId && (isLoading || transitioning)) {
    return <WebThreadLoadingShell />;
  }

  if (threadId && isError && !data) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-neutral-600 dark:text-neutral-300">{t('chat.historyLoadError')}</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-full bg-black px-5 py-2 text-sm font-medium text-white dark:bg-white dark:text-black"
        >
          {t('chat.retryHistory')}
        </button>
      </div>
    );
  }

  return (
    <ChatProviderInner
      key={threadId ?? 'new'}
      mode={mode}
      companyId={mode === 'employer' ? companyId : undefined}
      threadId={threadId}
      onThreadCreated={onThreadCreated}
      initialMessages={initialMessages}
    >
      {children}
    </ChatProviderInner>
  );
}

function ChatProviderInner({
  children,
  mode,
  companyId,
  threadId,
  onThreadCreated,
  initialMessages,
}: ChatProviderProps & { companyId?: string; initialMessages: ThreadMessageLike[] }) {
  const runtime = useMastraRuntime(
    mode ?? 'seeker',
    threadId,
    onThreadCreated,
    initialMessages,
    companyId,
  );

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <AgentChatModeContext.Provider value={mode ?? 'seeker'}>
        {children}
      </AgentChatModeContext.Provider>
    </AssistantRuntimeProvider>
  );
}

function WebThreadLoadingShell() {
  return (
    <div className="flex h-full items-center justify-center px-6">
      <SpinnerGlyph color="var(--tabbio-text-secondary)" size={20} />
    </div>
  );
}
